import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";
import {
  useGetEvChargerServiceListByVendorQuery,
  useMeQuery,
  useUpdatePrivatePortalMutation,
} from "generated/graphql";
import Typography from "components/basic/typography";
import Button from "components/basic/button";
import TextField from "components/basic/textField";
import Switch from "components/basic/switch";
import { headerOffset } from "components/basic/header";
import { DEFAULT_PROFILE_TAB_HEIGHT } from "components/basic/tabs";
import { useSnack } from "hooks/useSnack";
import ServicePicker from "./components/ServicePicker";
import LanguagePicker from "./components/LanguagePicker";
import { vendorPortalUrl } from "./portalUrl";

/**
 * Where a vendor claims the link they hand to clients they sourced themselves.
 * That link opens an estimator scoped to this vendor alone — no competing
 * quotes, and no Vestigezero branding.
 *
 * Reads the vendor from `me` rather than the profile form state, because the
 * portal fields are saved by their own mutation and must reflect what the
 * server actually stored.
 */
const PrivateLinkDashboard: React.FC = () => {
  const theme = useTheme();
  const showSnack = useSnack();
  const { t } = useTranslation("profile");
  const [{ data: meData }] = useMeQuery();
  const vendor = meData?.me?.vendor;

  const [slug, setSlug] = useState("");
  const [{ fetching }, updatePrivatePortal] = useUpdatePrivatePortalMutation();

  const [{ data: servicesData }] = useGetEvChargerServiceListByVendorQuery({
    variables: { vendorId: vendor?.id ?? 0 },
    pause: !vendor?.id,
  });
  const services = servicesData?.getEVChargerServiceListByVendor ?? [];

  useEffect(() => {
    setSlug(vendor?.slug ?? "");
  }, [vendor?.slug]);

  const savedUrl = useMemo(
    () => (vendor?.slug ? vendorPortalUrl(vendor.slug) : null),
    [vendor?.slug],
  );

  const save = useCallback(
    async (variables: {
      slug?: string;
      enabled?: boolean;
      serviceId?: number;
      language?: string | null;
    }) => {
      const response = await updatePrivatePortal(variables);
      const result = response.data?.updatePrivatePortal;

      if (result?.errors?.length) {
        result.errors.forEach((error) =>
          showSnack(error.message ?? t("private_link.save_failed"), "error"),
        );
        return;
      }

      showSnack(t("private_link.updated"), "success");
    },
    [updatePrivatePortal, showSnack, t],
  );

  const copyLink = useCallback(() => {
    if (!savedUrl) return;
    navigator.clipboard.writeText(savedUrl);
    showSnack(t("private_link.copied"), "success");
  }, [savedUrl, showSnack, t]);

  if (!vendor) {
    return null;
  }

  return (
    <div
      style={{
        display: "flex",
        overflow: "auto",
        scrollbarWidth: "thin",
        width: "100%",
        height: `calc(100vh - ${headerOffset(DEFAULT_PROFILE_TAB_HEIGHT + 1)})`,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            maxWidth: "560px",
            // Padding belongs here, not on the stretched parent, where it adds
            // to the fixed viewport height and forces a scrollbar.
            padding: theme.spacing(3, 2),
            boxSizing: "border-box",
          }}
        >
          <Typography variant="h5">{t("private_link.title")}</Typography>
          <Typography
            variant="body2"
            style={{
              marginTop: theme.spacing(1),
              color: theme.palette.grey[700],
            }}
          >
            {t("private_link.intro")}
          </Typography>

          <div style={{ marginTop: theme.spacing(3) }}>
            <TextField
              name="slug"
              label={t("private_link.name")}
              value={slug}
              onChange={(event) => setSlug(event.target.value)}
              placeHolder="acme-electric"
            />
            <Button
              variant="contained"
              color="primary"
              disabled={fetching || !slug.trim()}
              style={{ marginTop: theme.spacing(1) }}
              onClick={() => save({ slug })}
            >
              {t("private_link.save_name")}
            </Button>
          </div>

          {savedUrl && (
            <div
              style={{
                marginTop: theme.spacing(3),
                padding: theme.spacing(2),
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: theme.spacing(2),
              }}
            >
              <Typography variant="body1" style={{ wordBreak: "break-all" }}>
                {savedUrl}
              </Typography>
              <Button variant="outlined" onClick={copyLink}>
                {t("private_link.copy")}
              </Button>
            </div>
          )}

          <ServicePicker
            services={services}
            selectedId={vendor.privatePortalService?.id ?? null}
            disabled={fetching}
            onSelect={(serviceId) => save({ serviceId })}
          />

          <LanguagePicker
            selected={vendor.portalLanguage ?? null}
            disabled={fetching}
            onSelect={(language) => save({ language })}
          />

          <div
            style={{
              marginTop: theme.spacing(4),
              display: "flex",
              alignItems: "center",
              gap: theme.spacing(2),
            }}
          >
            <Switch
              checked={Boolean(vendor.privatePortalEnabled)}
              onChange={(checked) => save({ enabled: checked })}
              disabled={fetching || !vendor.slug}
            />
            <Typography variant="body1">
              {t(
                vendor.privatePortalEnabled
                  ? "private_link.live"
                  : "private_link.off",
              )}
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivateLinkDashboard;
